





































































































































































































































import {
  defineComponent,
  computed,
  toRefs,
  PropType,
  watch,
  ref,
  onBeforeUnmount,
  nextTick,
  onMounted,
  useRoute,
  vue3Model
} from '~/utils/nuxt3-migration'
import { PAGE_NS, PageState } from '~/store/modules/shared/page/state'
import { useNamespacedStore } from '~/compositions/store'
import { useModalToggle } from '~/compositions/modal-toggle'
import { useI18n } from '~/compositions/i18n'
import { TranslateResult } from 'vue-i18n'
import { getRouteMetaItem } from '~/utils/router'
import { ButtonSize, ButtonVariant } from '~/models/app/button'
import { ModalSize } from '~/models/app/modal'
import { useUserAgent } from '~/compositions/user-agent'
import { ciTimes } from '~/icons/source/regular/times'

export default defineComponent({
  model: vue3Model,
  props: {
    modelValue: { type: Boolean, default: false },
    size: { type: String as PropType<ModalSize>, default: 'md' },
    buttonSize: { type: String as PropType<ButtonSize>, default: 'md' },
    title: { type: String as PropType<string | TranslateResult>, default: '' },
    centered: { type: Boolean, default: true },
    hideBackdrop: { type: Boolean, default: false },
    disableAnimations: { type: Boolean, default: false },
    noCloseOnBackdrop: { type: Boolean, default: false },
    noCloseOnEsc: { type: Boolean, default: false },
    hideFooter: { type: Boolean, default: false },
    hideHeader: { type: Boolean, default: false },
    visible: { type: Boolean, default: false },
    okDisabled: { type: Boolean, default: false },
    cancelDisabled: { type: Boolean, default: false },
    hideHeaderClose: { type: Boolean, default: false },
    cancelTitle: { type: String, default: '' },
    okTitle: { type: String, default: 'OK' },
    okVariant: {
      type: String as PropType<ButtonVariant>,
      default: 'primary'
    },
    okOutline: {
      type: Boolean,
      required: false,
      default: false
    },
    cancelVariant: {
      type: String as PropType<ButtonVariant>,
      default: 'light'
    },
    cancelOutline: {
      type: Boolean,
      required: false,
      default: true
    },
    dialogClass: { type: [String, Array, Object], default: null },
    containerClass: { type: [String, Array, Object], default: null },
    contentClass: { type: [String, Array, Object], default: null },
    headerClass: { type: [String, Array, Object], default: null },
    closeIconClass: { type: [String, Array, Object], default: null },
    headerContainerClass: { type: [String, Array, Object], default: null },
    bodyClass: { type: [String, Array, Object], default: null },
    bodyId: { type: [String, Array, Object], default: undefined },
    footerClass: { type: [String, Array, Object], default: null },
    returnFocus: { type: null, default: null },
    scrollable: { type: Boolean, default: false },
    hideOk: { type: Boolean, default: false },
    hideCancel: { type: Boolean, default: false },
    modalToggle: { type: Boolean, default: false },
    static: { type: Boolean, default: false },
    hash: { type: String, default: undefined },
    modalInSearch: { type: Boolean, default: false },
    iframeMode: { type: Boolean, default: false }, // used when modal content is iframe
    displayOnlyLastModal: { type: Boolean, default: true }, // Used for multiple modals
    loading: { type: Boolean, default: false },
    fullscreenMobile: { type: Boolean, default: false },
    fullWidth: { type: Boolean, default: false },
    mobileDrawer: { type: Boolean, default: false },
    drawerDraggable: { type: Boolean, default: false },
    icon: { type: Object, default: null },
    mobileDrawerMaxHeightClass: {
      type: String,
      default: 'max-sm:tw-h-[85vh] max-sm:!tw-h-[85dvh]'
    }
  },

  setup(props, { emit, slots }) {
    const {
      size,
      visible,
      noCloseOnBackdrop,
      noCloseOnEsc,
      returnFocus,
      static: staticProp,
      modalToggle,
      hash,
      modalInSearch,
      cancelTitle,
      fullWidth,
      hideHeader,
      iframeMode,
      title,
      hideFooter,
      hideHeaderClose,
      displayOnlyLastModal
    } = toRefs(props)
    const modalToggleFunc = useModalToggle(
      () => closeModal(null, true),
      () => emit('after-modal-close'),
      noCloseOnEsc
    )

    modalToggleFunc.setHash(hash.value)
    modalToggleFunc.setModalInSearch(modalInSearch.value)
    const { dispatch } = useNamespacedStore<PageState>(PAGE_NS)
    const internalValue = ref(props.modelValue || visible.value)
    const modalContainerRef = ref(null)
    const modalSubContainerRef = ref(null)
    const preventDefaultFlag = ref(false)
    const animate = ref(false)
    const animating = ref(false)
    const body = ref('body')
    const translateY = ref(100)
    const isDragging = ref(false)
    const internalDisableTransitions = ref(false)
    const startY = ref(0)
    const modalHeight = ref(0)
    const modalRef = ref(null)
    const { t } = useI18n()
    const cancelTitleToShow = computed(() => {
      return cancelTitle.value || t('cancel')
    })
    const isScrollable = computed(() => {
      return props.scrollable || props.mobileDrawer
    })

    const animationsDisabled = computed(
      () => props.disableAnimations || internalDisableTransitions.value
    )

    const animationDuration = computed(() => {
      if (animationsDisabled.value) {
        return undefined
      }
      return 300 // ms
    })

    const route = useRoute()
    const inIframe = computed(() => {
      return Boolean(route.value?.meta?.isIframe)
    })

    const { isMobile } = useUserAgent()

    const isCrossOrigin = computed(() => {
      return getRouteMetaItem(route.value, 'isCrossOrigin')
    })

    const showHeader = computed(() => {
      if (hideHeader.value || iframeMode.value) {
        return false
      }

      return (
        (!hasCustomHeader.value && !hideHeaderClose.value) ||
        hasCustomHeader.value ||
        hasCustomTitle.value ||
        title.value
      )
    })

    const showFooter = computed(() => {
      return !hideFooter.value || hasCustomFooter.value
    })

    onMounted(() => {
      if (internalValue.value) {
        showModal()
      }
    })

    watch(
      () => props.modelValue,
      newValue => {
        if (newValue) {
          showModal()
        } else if (internalValue.value) {
          closeModal()
        }
      }
    )

    watch(hash, () => {
      modalToggleFunc.setHash(hash.value)
    })

    watch(modalInSearch, () => {
      modalToggleFunc.setModalInSearch(modalInSearch.value)
    })

    watch(visible, () => {
      if (visible.value) {
        showModal()
      } else if (internalValue.value) {
        closeModal()
      }
    })

    onBeforeUnmount(() => {
      closeModal('unmount')
    })

    const handleClickBackdrop = () => {
      if (animating.value) {
        return
      }
      if (!noCloseOnBackdrop.value) {
        closeModal('backdrop')
      }
    }

    const closeModal = async (
      trigger: string = null,
      fromCallback: boolean = false
    ) => {
      await nextTick()
      if (!internalValue.value) {
        return
      }
      if (preventDefaultFlag.value) {
        preventDefaultFlag.value = false
        return
      }
      animate.value = false
      if (props.mobileDrawer) {
        translateY.value = 100
      }
      if (animationDuration.value) {
        animating.value = true
        await new Promise(resolve => {
          setTimeout(() => {
            animating.value = false
            resolve()
          }, animationDuration.value)
        })
      }

      emit('hide', { preventDefault, trigger })
      if (modalToggle.value && !fromCallback) {
        modalToggleFunc.onModalHide({ trigger })
      }

      internalValue.value = false
      if (openModalsCount() <= 1 || staticProp.value) {
        removeOpenModalClass()

        if (inIframe.value) {
          handleIframeHide()
        }

        removeBodyHeaderMargin()
        returnFocusToElement()
      }
      removeFromBody()
      emit('update:modelValue', false)
      emit('hidden', false)
      showLastModal()
    }

    const handleCloseOnEsc = () => {
      if (noCloseOnEsc.value || modalToggle.value) {
        // if we have modal toggle escape will be handled by it
        return
      }
      closeModal('esc')
    }
    const handleIframeHide = () => {
      if (!isCrossOrigin.value && window.parent && window.parent !== window) {
        window.parent.document.body.classList.remove('modal-open')
      }
    }
    const addOpenModalClass = () => {
      if (!process.client) return
      setBodyHeaderMargin()
      if (modalContainerRef.value) {
        modalContainerRef.value.focus()
      }
      dispatch('addBodyClass', 'modal-open')
    }
    const removeOpenModalClass = () => {
      dispatch('removeBodyClass', 'modal-open')
    }
    const handleCloseBtn = (e: Event) => {
      if (animating.value) {
        return
      }

      emit('close', { preventDefault })
      if (!e?.defaultPrevented) {
        closeModal('headerclose')
      } else if (e?.preventDefault) {
        e.preventDefault()
      }
    }

    const bodyScrollbarWidth = () => {
      if (inIframe.value && !isCrossOrigin.value) {
        return (
          window.parent.innerWidth - window.parent.document.body.offsetWidth
        )
      }
      return window.innerWidth - document.body.offsetWidth
    }
    const setBodyHeaderMargin = () => {
      if (!document.body.style.marginRight) {
        const rightMargin = bodyScrollbarWidth()
        document.body.style.marginRight = `${rightMargin}px`
      }
    }
    const removeBodyHeaderMargin = () => {
      document.body.style.marginRight = null
    }
    const hasCustomFooter = computed(() => {
      return !!slots['modal-footer']
    })

    const hasCustomHeader = computed(() => {
      return !!slots['modal-header']
    })

    const hasCustomTitle = computed(() => {
      return !!slots['modal-title']
    })

    if (props.modelValue) {
      addOpenModalClass()
    }

    const handleOk = (e: Event) => {
      if (animating.value) {
        return
      }
      emit('ok', { preventDefault })
      if (!e?.defaultPrevented) {
        closeModal('ok')
      } else if (e?.preventDefault) {
        e.preventDefault()
      }
    }
    const handleCancel = (e: Event) => {
      if (animating.value) {
        return
      }
      emit('cancel', { preventDefault })
      if (!e?.defaultPrevented) {
        closeModal('cancel')
      } else if (e?.preventDefault) {
        e.preventDefault()
      }
    }

    const showModal = async () => {
      await hideAllOtherModals()
      emit('show', { preventDefault })
      if (modalToggle.value) {
        modalToggleFunc.modalOpen()
      }
      if (preventDefaultFlag.value) {
        preventDefaultFlag.value = false
        return
      }

      internalValue.value = true

      if (!staticProp.value) {
        await appendToBody()
      }
      addOpenModalClass()

      animate.value = true
      if (props.mobileDrawer) {
        translateY.value = 0
      }
      if (animationDuration.value) {
        animating.value = true
        await new Promise(resolve => {
          setTimeout(() => {
            animating.value = false
            resolve()
          }, animationDuration.value)
        })
      }

      emit('update:modelValue', true)
      emit('shown')
    }
    // Used for multiple modals
    const hideAllOtherModals = async () => {
      if (!displayOnlyLastModal.value) {
        return
      }
      await nextTick()
      const arrayOfModals = getAllModals()

      if (arrayOfModals.length) {
        internalDisableTransitions.value = true
        arrayOfModals.map(m => (m.style.display = 'none'))
      } else {
        internalDisableTransitions.value = false
      }
    }

    // Used for multiple modals
    const showLastModal = () => {
      const arrayOfModals = getAllModals()
      if (arrayOfModals.length) {
        arrayOfModals[arrayOfModals.length - 1].style.display = ''
      } else {
        internalDisableTransitions.value = false
      }
    }

    const getAllModals = () => {
      return Array.prototype.slice
        .call(document.body.children)
        .filter(c => c.localName === 'div' && c.dataset.modal)
    }

    const appendToBody = async () => {
      await nextTick()
      document.querySelector('body').appendChild(modalContainerRef.value)
    }

    const removeFromBody = () => {
      if (modalContainerRef?.value?.remove && !staticProp.value) {
        modalContainerRef.value.remove()
      }
    }

    const openModalsCount = () => {
      return Array.from(document.body.children).filter(
        (c: HTMLElement) => c.dataset.modal
      ).length
    }

    const returnFocusToElement = () => {
      if (returnFocus.value && typeof returnFocus.value === 'object') {
        returnFocus.value.focus()
      } else if (typeof returnFocus.value === 'string') {
        const el = document.getElementById(returnFocus.value.replace('#', ''))
        if (el) {
          el.focus()
        }
      }
    }
    const sizeClass = computed(() => {
      if (fullWidth.value) {
        return 'tw-w-full tw-max-w-[100%]'
      }

      switch (size.value) {
        case 'xl':
          return 'xl:tw-max-w-[1202px] md:tw-max-w-[1032px] sm:tw-max-w-[564px] tw-min-w-0 tw-w-full'
        case 'lg':
          return 'lg:tw-max-w-[862px] md:tw-max-w-[564px] sm:tw-max-w-[564px] tw-min-w-0 tw-w-full'
        case 'sm':
          return 'sm:tw-max-w-[452px] tw-min-w-0 tw-w-full'
        default:
          return `sm:tw-max-w-[564px] tw-min-w-0`
      }
    })

    const preventDefault = () => (preventDefaultFlag.value = true)

    function startDrag(event) {
      if (process.server) {
        return
      }

      if (modalRef.value) {
        modalHeight.value = modalRef.value.offsetHeight
      }

      isDragging.value = true
      startY.value = event.touches ? event.touches[0].clientY : event.clientY

      window.addEventListener('mousemove', onDrag)
      window.addEventListener('touchmove', onDrag, { passive: false })
      window.addEventListener('mouseup', stopDrag)
      window.addEventListener('touchend', stopDrag)
    }

    function onDrag(event) {
      if (!isDragging.value || process.server || !modalHeight.value) {
        return
      }

      const currentY = event.touches ? event.touches[0].clientY : event.clientY
      const delta = currentY - startY.value

      // Convert pixel drag to percentage of modal height
      const deltaPercent = (delta / modalHeight.value) * 100
      translateY.value = Math.min(Math.max(deltaPercent, 0), 100)
    }

    function stopDrag() {
      if (process.server) {
        return
      }

      isDragging.value = false

      const closeThreshold = 10 // 10% of modal height

      if (translateY.value > closeThreshold) {
        closeModal()
      } else {
        translateY.value = 0 // Snap back
      }

      window.removeEventListener('mousemove', onDrag)
      window.removeEventListener('touchmove', onDrag)
      window.removeEventListener('mouseup', stopDrag)
      window.removeEventListener('touchend', stopDrag)
    }

    const appliedStyle = computed(() => {
      const style = {}

      if (animationDuration.value) {
        style.transitionDuration = animationDuration.value + 'ms'
      }
      if (props.mobileDrawer) {
        style.transform = `translateY(${translateY.value}%)`
      }
      return style
    })

    function scrollModalToTop() {
      if (!isScrollable.value && modalSubContainerRef.value) {
        modalSubContainerRef.value.scrollTop = 0
      }
    }

    return {
      ciTimes,
      sizeClass,
      hasCustomFooter,
      hasCustomHeader,
      hasCustomTitle,
      internalValue,
      modalContainerRef,
      modalSubContainerRef,
      handleCloseOnEsc,
      animate,
      handleClickBackdrop,
      handleCloseBtn,
      closeModal,
      handleOk,
      handleCancel,
      showModal,
      hide: closeModal,
      show: showModal,
      body,
      cancelTitleToShow,
      inIframe,
      handleIframeHide,
      isMobile,
      showHeader,
      isScrollable,
      scrollModalToTop,
      startDrag,
      onDrag,
      stopDrag,
      translateY,
      isDragging,
      animationDuration,
      animationsDisabled,
      appliedStyle,
      modalRef,
      showFooter
    }
  }
})
